import React, { useState } from 'react';
import Navigation from '../components/Navigation';
import { useAuth } from '../hooks/useAuth';
import { useTemplates } from '../hooks/useTemplates';
import { Plus, Edit, Trash2, Eye, Copy } from 'lucide-react';
import { ExerciseTemplate } from '../types';
import ViewTemplateModal from '../components/ViewTemplateModal';

const TemplatesPage: React.FC = () => {
  const { trainer, logout } = useAuth();
  const { templates, isLoading, deleteTemplate, isDeleteLoading } = useTemplates();
  
  // Состояние для модального окна просмотра шаблона
  const [isViewTemplateModalOpen, setIsViewTemplateModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<ExerciseTemplate | null>(null);

  const handleEditTemplate = (id: number) => {
    // TODO: Реализовать редактирование шаблона
    console.log('Редактирование шаблона:', id);
  };

  const handleDeleteTemplate = async (id: number) => {
    if (window.confirm('Вы уверены, что хотите удалить этот шаблон?')) {
      deleteTemplate(id);
    }
  };

  const handleViewTemplate = (template: ExerciseTemplate) => {
    setSelectedTemplate(template);
    setIsViewTemplateModalOpen(true);
  };

  const handleCloseViewTemplateModal = () => {
    setIsViewTemplateModalOpen(false);
    setSelectedTemplate(null);
  };

  const handleCopyTemplate = (id: number) => {
    // TODO: Реализовать копирование шаблона
    console.log('Копирование шаблона:', id);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation currentPage="templates" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Шаблоны упражнений</h1>
              <p className="mt-2 text-gray-600">
                Создавайте и управляйте наборами упражнений для быстрого создания тренировок
              </p>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-12">
            <div className="mx-auto h-12 w-12 text-gray-400">
              <Copy className="h-12 w-12" />
            </div>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Нет шаблонов</h3>
            <p className="mt-1 text-sm text-gray-500">
              Создайте шаблон из тренировки, нажав кнопку "Сохранить как шаблон" в заголовке упражнений клиента.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {templates.map((template: ExerciseTemplate) => (
              <div
                key={template.id}
                className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow duration-200"
              >
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900 truncate">
                      {template.name}
                    </h3>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleViewTemplate(template)}
                        className="p-1 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                        title="Просмотр"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEditTemplate(template.id)}
                        className="p-1 text-gray-400 hover:text-blue-600 transition-colors duration-200"
                        title="Редактировать"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleCopyTemplate(template.id)}
                        className="p-1 text-gray-400 hover:text-green-600 transition-colors duration-200"
                        title="Копировать"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteTemplate(template.id)}
                        disabled={isDeleteLoading}
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors duration-200 disabled:opacity-50"
                        title="Удалить"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  {template.description && (
                    <p className="mt-2 text-sm text-gray-600 line-clamp-2">
                      {template.description}
                    </p>
                  )}
                  
                  <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
                    <span>{template.template_exercises?.length || 0} упражнений</span>
                    <span>
                      {new Date(template.created_at).toLocaleDateString('ru-RU')}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <ViewTemplateModal
        isOpen={isViewTemplateModalOpen}
        onClose={handleCloseViewTemplateModal}
        template={selectedTemplate}
      />
    </div>
  );
};

export default TemplatesPage; 